/**
 * Utilitaire pour charger un cubemap Three.js avec rotation de la face bottom
 * Basé sur l'analyse de InteriorView.js
 */

import { CubeTexture, TextureLoader, ClampToEdgeWrapping, LinearFilter } from 'three'
import type { CubeMapFaces } from '@/types/interior.types'

/** Événements de progression du chargement */
export interface CubeMapLoadProgress {
  /** Nombre de faces chargées */
  loaded: number
  /** Nombre total de faces */
  total: number
  /** Pourcentage de progression (0-100) */
  percentage: number
  /** URL de la face actuellement chargée */
  currentUrl?: string
}

/** Options de chargement du cubemap */
export interface CubeMapLoaderOptions {
  /** Callback de progression */
  onProgress?: (progress: CubeMapLoadProgress) => void
  /** Callback d'erreur */
  onError?: (error: Error) => void
}

/**
 * Applique une rotation de 90° à une image
 * Utilisé pour les faces top et bottom du cubemap
 *
 * @param image - Image HTMLImageElement à transformer
 * @returns Nouvelle image avec rotation appliquée
 */
function rotateImage90(image: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = image.height
  canvas.height = image.width

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Impossible de créer le contexte 2D du canvas')
  }

  // Translater au centre, rotation 90°, translater en arrière
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate(Math.PI / 2) // 90° en radians
  ctx.drawImage(image, -image.width / 2, -image.height / 2)

  return canvas
}

/**
 * Charge un cubemap Three.js avec gestion de la rotation des faces top et bottom
 *
 * Ordre des faces dans Three.js CubeTexture :
 * 0: +X (right)
 * 1: -X (left)
 * 2: +Y (top) <- rotation 90° appliquée
 * 3: -Y (bottom) <- rotation 90° appliquée
 * 4: +Z (front)
 * 5: -Z (back)
 *
 * @param faces - URLs des 6 faces du cubemap
 * @param options - Options de chargement
 * @returns Promise qui résout en CubeTexture
 */
export async function loadCubeMap(
  faces: CubeMapFaces | string[],
  options: CubeMapLoaderOptions = {}
): Promise<CubeTexture> {
  const { onProgress, onError } = options

  // Normaliser les faces en tableau dans l'ordre Three.js
  const urls: string[] = Array.isArray(faces)
    ? faces
    : [
        faces.right,   // 0: +X
        faces.left,    // 1: -X
        faces.top,     // 2: +Y
        faces.bottom,  // 3: -Y
        faces.front,   // 4: +Z
        faces.back     // 5: -Z
      ]

  if (urls.length !== 6) {
    const error = new Error(`Le cubemap doit contenir exactement 6 faces (reçu: ${urls.length})`)
    onError?.(error)
    throw error
  }
  
  const loader = new TextureLoader()
  const images: (HTMLImageElement | HTMLCanvasElement)[] = []
  let loadedCount = 0
  
  // Fonction pour émettre la progression
  const emitProgress = (currentUrl?: string) => {
    const percentage = Math.round((loadedCount / 6) * 100)
    onProgress?.({
      loaded: loadedCount,
      total: 6,
      percentage,
      currentUrl
    })
  }
  
  // Charger chaque face
  const loadPromises = urls.map((url, index) => {
    return new Promise<HTMLImageElement | HTMLCanvasElement>((resolve, reject) => {
      loader.load(
        url,
        (texture) => {
          let image: HTMLImageElement | HTMLCanvasElement = texture.image as HTMLImageElement
          
          // Appliquer rotation 90° pour les faces top et bottom
          // Cela corrige l'orientation des textures après le changement de position de la caméra
          if (index === 2 || index === 3) {
            image = rotateImage90(image)
          }
          
          loadedCount++
          emitProgress(url)
          resolve(image)
        },
        undefined, // onProgress non utilisé ici
        (error) => {
          const errorMessage = error instanceof Error ? error.message : String(error)
          const loadError = new Error(`Erreur de chargement de ${url}: ${errorMessage}`)
          onError?.(loadError)
          reject(loadError)
        }
      )
    })
  })
  
  try {
    // Attendre que toutes les faces soient chargées
    const loadedImages = await Promise.all(loadPromises)
    images.push(...loadedImages)
    
    // Créer la CubeTexture
    const cubeTexture = new CubeTexture(images)
    
    // Configuration de la texture
    cubeTexture.needsUpdate = true
    cubeTexture.wrapS = ClampToEdgeWrapping
    cubeTexture.wrapT = ClampToEdgeWrapping
    cubeTexture.minFilter = LinearFilter
    cubeTexture.magFilter = LinearFilter
    
    return cubeTexture
  } catch (error) {
    const finalError = error instanceof Error 
      ? error 
      : new Error('Erreur inconnue lors du chargement du cubemap')
    onError?.(finalError)
    throw finalError
  }
}

/**
 * Précharge les images du cubemap sans créer la texture
 * Utile pour le préchargement anticipé
 * 
 * @param faces - URLs des 6 faces du cubemap
 * @param options - Options de chargement
 * @returns Promise qui résout quand toutes les images sont chargées
 */
export async function preloadCubeMapImages(
  faces: CubeMapFaces | string[],
  options: CubeMapLoaderOptions = {}
): Promise<void> {
  const { onProgress, onError } = options
  
  // Normaliser les faces en tableau
  const urls: string[] = Array.isArray(faces)
    ? faces
    : [faces.right, faces.left, faces.top, faces.bottom, faces.front, faces.back]
  
  let loadedCount = 0
  
  const emitProgress = (currentUrl?: string) => {
    const percentage = Math.round((loadedCount / 6) * 100)
    onProgress?.({
      loaded: loadedCount,
      total: 6,
      percentage,
      currentUrl
    })
  }
  
  const loadPromises = urls.map((url) => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        loadedCount++
        emitProgress(url)
        resolve()
      }
      
      img.onerror = () => {
        const error = new Error(`Erreur de préchargement de ${url}`)
        onError?.(error)
        reject(error)
      }
      
      img.src = url
    })
  })
  
  try {
    await Promise.all(loadPromises)
  } catch (error) {
    const finalError = error instanceof Error
      ? error
      : new Error('Erreur inconnue lors du préchargement du cubemap')
    onError?.(finalError)
    throw finalError
  }
}